/**
 * Section — DESIGN (Goal Game).
 *
 * Writes the SAME shared gift-setup fields the Voucher Design screen writes
 * (headline_font, body_font, headline_size, body_size, the colours and the
 * button accent, wallpaper_overlay, celebration_enabled), through the existing
 * /goal-game/admin/branding endpoint. There is no Goal-Game-only copy of these
 * settings, and everything is saved per gift setup.
 *
 * Nothing saved = the Red, White and Blue default on the fan screens.
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Save, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../../lib/api';
import { DESIGN_PRESETS, FONT_CHOICES, SCALE_DEFAULT, SCALE_MAX, SCALE_MIN, TEXT_SIZES, loadFont } from '../../../lib/designTokens';
import { CARD_STYLES } from '../../shared/cardSurface';
import { btn } from './buttons';

const field = "w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-sm";

// The same three surfaces the cards use, named for an operator.
const BUTTON_STYLE_OPTIONS = [
    { key: '', label: 'Default (solid)' },
    ...CARD_STYLES.map((key) => ({
        key,
        label: { frosted: 'Frosted (translucent + blur)',
                 clear: 'Clear (see-through with an outline)',
                 solid: 'Solid (opaque)' }[key],
    })),
];

const Color = ({ label, value, onChange, testid, note }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
        <div className="flex items-center gap-2">
            <input type="color" value={value || '#000000'} onChange={(e) => onChange(e.target.value)}
                   data-testid={testid}
                   className="h-9 w-12 rounded border border-slate-200 dark:border-slate-700 bg-transparent" />
            <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
                   placeholder="(default)" className={`${field} font-mono`} />
            {value && (
                <button type="button" onClick={() => onChange('')} data-testid={`${testid}-clear`}
                        className="text-xs text-slate-500 underline shrink-0">clear</button>
            )}
        </div>
        {/* What this colour actually changes on the fan's win screen. */}
        {note && (
            <span className="mt-1 block text-[11px] text-slate-500" data-testid={`${testid}-note`}>
                {note}
            </span>
        )}
    </label>
);

const Pick = ({ label, value, options, onChange, testid }) => (
    <label className="block">
        <span className="block text-xs font-medium text-slate-500 mb-1">{label}</span>
        <select value={value || ''} onChange={(e) => onChange(e.target.value)}
                data-testid={testid} className={field}>
            {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
    </label>
);

export const GoalDesignBlock = ({ cfg, orgId, promotionId, onReload, onDraft }) => {
    const { t } = useTranslation();
    const setupId = cfg.resolved_setup_id;
    const [g, setG] = useState(null);
    const [busy, setBusy] = useState(false);
    const [failed, setFailed] = useState(false);

    const load = useCallback(async () => {
        // No setup, or a setup that will not load: say so. Never spin for ever.
        if (!setupId) { setG(null); setFailed(true); return; }
        try {
            const res = await api.get(`/voucher-settings/gift-setups/${setupId}`,
                                      { params: { organization_id: orgId } });
            setG(res.data.config || {});
            setFailed(false);
        } catch (_e) {
            setG(null);
            setFailed(true);
        }
    }, [setupId, orgId]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        loadFont(g?.headline_font);
        loadFont(g?.body_font);
    }, [g]);
    // Every keystroke and every slider goes straight to the phone preview,
    // before anything is saved.
    useEffect(() => { if (g) onDraft?.(g); }, [g, onDraft]);

    const set = (k, v) => setG((p) => ({ ...(p || {}), [k]: v }));

    const applyPreset = (p) => setG((prev) => ({
        ...(prev || {}),
        voucher_design_mode: p.bg ? 'custom' : 'existing',
        gg_voucher_bg: p.bg || '',
        gg_voucher_text: p.text || '',
        gg_voucher_accent: p.accent || '',
        gg_voucher_button: p.button || '',
    }));

    const save = async () => {
        setBusy(true);
        try {
            await api.post('/goal-game/admin/branding', {
                promotion_id: promotionId || null,
                organization_id: orgId,
                setup_id: setupId,
                gift: {
                    voucher_design_mode: g.voucher_design_mode === 'custom' ? 'custom' : 'existing',
                    gg_voucher_bg: g.gg_voucher_bg || '',
                    gg_voucher_text: g.gg_voucher_text || '',
                    gg_voucher_accent: g.gg_voucher_accent || '',
                    gg_voucher_button: g.gg_voucher_button || '',
                    gg_button_style: g.gg_button_style || '',
                    gg_scores_button: g.gg_scores_button || '',
                    gg_standings_button: g.gg_standings_button || '',
                    gg_tonight_button: g.gg_tonight_button || '',
                    gg_stats_button: g.gg_stats_button || '',
                    gg_scorebar_text: g.gg_scorebar_text || '',
                    headline_font: g.headline_font || '',
                    body_font: g.body_font || '',
                    headline_size: g.headline_size || '',
                    body_size: g.body_size || '',
                    headline_scale: g.headline_scale || SCALE_DEFAULT,
                    body_scale: g.body_scale || SCALE_DEFAULT,
                    wallpaper_overlay: g.wallpaper_overlay ?? '',
                    celebration_enabled: g.celebration_enabled !== false,
                },
            });
            await onReload();
            await load();
            toast.success('Design saved');
        } catch (_e) { toast.error('Could not save the design'); }
        finally { setBusy(false); }
    };

    if (!g) {
        // Plain words and a way out — the old build spun here for ever when an
        // org had no gift setup at all.
        if (failed) {
            return (
                <div className="pt-3 space-y-3" data-testid="gg-design-empty">
                    <p className="text-sm text-slate-500">
                        The design for this organization could not be loaded — it may have no gift
                        setup yet. Creating one takes a second and starts from the Red, White and
                        Blue default.
                    </p>
                    <button type="button" data-testid="gg-design-retry"
                            onClick={async () => { setFailed(false); await onReload?.(); await load(); }}
                            className="rounded-lg bg-slate-900 dark:bg-white dark:text-slate-900 text-white px-3 py-1.5 text-sm font-semibold">
                        Create setup and try again
                    </button>
                </div>
            );
        }
        return <div className="pt-3"><Loader2 className="animate-spin text-slate-400" size={18} /></div>;
    }

    const overlay = g.wallpaper_overlay == null ? 45 : g.wallpaper_overlay;
    const headlineScale = g.headline_scale || SCALE_DEFAULT;
    const bodyScale = g.body_scale || SCALE_DEFAULT;

    return (
        <div className="space-y-5 pt-3">
            <p className="text-xs text-slate-500">
                Saved to the gift setup <strong>{cfg.resolved_setup_name}</strong> — the same fields the
                Voucher Design screen uses. Empty means the Red, White and Blue default.
            </p>

            <div>
                <p className="text-xs font-medium text-slate-500 mb-2">Theme</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="gg-design-presets">
                    {DESIGN_PRESETS.map((p) => (
                        <button key={p.key} type="button" onClick={() => applyPreset(p)}
                                data-testid={`gg-design-preset-${p.key}`}
                                className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-700 px-2.5 py-2 text-xs font-medium hover:bg-slate-100 dark:hover:bg-slate-800">
                            <span className="flex -space-x-1 shrink-0">
                                {p.swatch.map((sw, i) => (
                                    <span key={i} className="w-3.5 h-3.5 rounded-full border border-white/40"
                                          style={{ backgroundColor: sw }} />
                                ))}
                            </span>
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <Color label="Background" value={g.gg_voucher_bg} note={t('goalGame.colourBg')}
                       onChange={(v) => set('gg_voucher_bg', v)} testid="gg-design-bg" />
                <Color label="Text" value={g.gg_voucher_text} note={t('goalGame.colourText')}
                       onChange={(v) => set('gg_voucher_text', v)} testid="gg-design-text" />
                <Color label="Accent (highlights)" value={g.gg_voucher_accent} note={t('goalGame.colourAccent')}
                       onChange={(v) => set('gg_voucher_accent', v)} testid="gg-design-accent" />
                <Color label="Button colour (primary actions)" value={g.gg_voucher_button} note={t('goalGame.colourButton')}
                       onChange={(v) => set('gg_voucher_button', v)} testid="gg-design-button" />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <Pick label="Button style" value={g.gg_button_style} options={BUTTON_STYLE_OPTIONS}
                      onChange={(v) => set('gg_button_style', v)} testid="gg-design-button-style" />
            </div>

            {/* The bar at the bottom of the fan screen. All three unset = today. */}
            <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Score bar buttons</p>
                <div className="grid sm:grid-cols-3 gap-3">
                    <Color label="Live scores button colour" value={g.gg_scores_button}
                           onChange={(v) => set('gg_scores_button', v)} testid="gg-design-scores-button" />
                    <Color label="Standings button colour" value={g.gg_standings_button}
                           onChange={(v) => set('gg_standings_button', v)} testid="gg-design-standings-button" />
                    <Color label="Score bar text colour" value={g.gg_scorebar_text}
                           onChange={(v) => set('gg_scorebar_text', v)} testid="gg-design-scorebar-text" />
                </div>
            </div>

            {/* The two pills on the fan screen, same controls as the score bar. */}
            <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Contest and stats buttons</p>
                <div className="grid sm:grid-cols-3 gap-3">
                    <Color label="Tonight's contest button colour" value={g.gg_tonight_button}
                           onChange={(v) => set('gg_tonight_button', v)} testid="gg-design-tonight-button" />
                    <Color label="Tonight's stats button colour" value={g.gg_stats_button}
                           onChange={(v) => set('gg_stats_button', v)} testid="gg-design-stats-button" />
                </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
                <Pick label="Headline font" value={g.headline_font} options={FONT_CHOICES}
                      onChange={(v) => set('headline_font', v)} testid="gg-design-headline-font" />
                <Pick label="Body font" value={g.body_font} options={FONT_CHOICES}
                      onChange={(v) => set('body_font', v)} testid="gg-design-body-font" />
                <Pick label="Headline size" value={g.headline_size} options={TEXT_SIZES}
                      onChange={(v) => set('headline_size', v)} testid="gg-design-headline-size" />
                <Pick label="Body text size" value={g.body_size} options={TEXT_SIZES}
                      onChange={(v) => set('body_size', v)} testid="gg-design-body-size" />
            </div>

            {/* The step above sets the shape; these two tune it, exactly like
                the voucher design screen. 100 = the step as it renders today. */}
            <div className="grid sm:grid-cols-2 gap-3 max-w-xl">
                <label className="block">
                    <span className="block text-xs font-medium text-slate-500 mb-1">
                        Headline fine size — {headlineScale}%
                    </span>
                    <input type="range" min={SCALE_MIN} max={SCALE_MAX} step={5} value={headlineScale}
                           onChange={(e) => set('headline_scale', Number(e.target.value))}
                           data-testid="gg-design-headline-scale" className="w-full" />
                </label>
                <label className="block">
                    <span className="block text-xs font-medium text-slate-500 mb-1">
                        Body fine size — {bodyScale}%
                    </span>
                    <input type="range" min={SCALE_MIN} max={SCALE_MAX} step={5} value={bodyScale}
                           onChange={(e) => set('body_scale', Number(e.target.value))}
                           data-testid="gg-design-body-scale" className="w-full" />
                </label>
            </div>

            <label className="block max-w-sm">
                <span className="block text-xs font-medium text-slate-500 mb-1">
                    Wallpaper darkening — {overlay}% (keeps text readable on a bright photo)
                </span>
                <input type="range" min={0} max={85} value={overlay}
                       onChange={(e) => set('wallpaper_overlay', Number(e.target.value))}
                       data-testid="gg-design-overlay" className="w-full" />
            </label>

            <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={g.celebration_enabled !== false}
                       onChange={(e) => set('celebration_enabled', e.target.checked)}
                       data-testid="gg-design-celebration" />
                <Sparkles size={14} className="text-amber-500" />
                Win celebration (burst and glow on the win screen)
            </label>

            <button type="button" onClick={save} disabled={busy} data-testid="gg-design-save"
                    className={btn.primary}>
                {busy ? <Loader2 className="animate-spin" size={15} /> : <Save size={15} />}
                Save design
            </button>
            <p className="text-xs text-slate-500">
                The burst is CSS only and renders nothing for a phone set to reduced motion.
                Fan screens read all of this when they load, so a change shows on the next tap.
            </p>
        </div>
    );
};

export default GoalDesignBlock;
